import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft, Check, ChevronLeft, ChevronRight, ChevronsDown, ChevronsUp, Copy, Crop, Eye,
  FlipHorizontal, Frame, Heart, ImagePlus, Link2, Maximize2, Plus, Redo2, RotateCcw, RotateCw,
  Trash2, Undo2, Wand2, X,
} from "lucide-react";

/*
  Pintasan keyboard:
  Ctrl+Z batalkan · Ctrl+Shift+Z / Ctrl+Y ulangi · Ctrl+D duplikat bingkai
  Delete hapus bingkai · Panah geser bingkai (Shift = lebih jauh) · Enter atur posisi foto · Esc batal pilih
  Tahan Alt saat menggeser bingkai untuk mematikan garis bantu (snap).
*/

/* ---------- Konfigurasi ---------- */
// Template dasar: grid 4 kolom x 2 baris pada area di dalam margin.
const LAYOUTS = [
  { id: "full", name: "Satu foto", slots: [["1/5", "1/3"]] },
  { id: "split", name: "Dua foto", slots: [["1/3", "1/3"], ["3/5", "1/3"]] },
  { id: "hero3", name: "Satu besar, dua kecil", slots: [["1/3", "1/3"], ["3/5", "1/2"], ["3/5", "2/3"]] },
  { id: "grid4", name: "Empat kotak", slots: [["1/3", "1/2"], ["3/5", "1/2"], ["1/3", "2/3"], ["3/5", "2/3"]] },
  { id: "strip4", name: "Empat vertikal", slots: [["1/2", "1/3"], ["2/3", "1/3"], ["3/4", "1/3"], ["4/5", "1/3"]] },
];
const AUTO_ORDER = ["hero3", "split", "strip4", "grid4", "full"];
const BGS = ["#FFFFFF", "#F4EFE6", "#E8E3D9", "#1C1C1C"];
const MIN = 6; // ukuran minimum bingkai (%)
const DEFAULT_AR = 1.5; // rasio sementara sebelum ukuran asli foto terbaca
const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_API_KEY; // untuk impor folder Drive
const RESET = { z: 1, ox: 0, oy: 0, rot: 0, tilt: 0, flip: false }; // pengaturan foto di dalam bingkai

const HANDLES = [
  ["nw", "-left-1.5 -top-1.5 cursor-nwse-resize"],
  ["n", "left-1/2 -top-1.5 -translate-x-1/2 cursor-ns-resize"],
  ["ne", "-right-1.5 -top-1.5 cursor-nesw-resize"],
  ["e", "-right-1.5 top-1/2 -translate-y-1/2 cursor-ew-resize"],
  ["se", "-bottom-1.5 -right-1.5 cursor-nwse-resize"],
  ["s", "-bottom-1.5 left-1/2 -translate-x-1/2 cursor-ns-resize"],
  ["sw", "-bottom-1.5 -left-1.5 cursor-nesw-resize"],
  ["w", "-left-1.5 top-1/2 -translate-y-1/2 cursor-ew-resize"],
];

const uid = () => Math.random().toString(36).slice(2, 9);
const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const layoutOf = (id) => LAYOUTS.find((l) => l.id === id) || LAYOUTS[0];

// Posisi & ukuran bingkai dalam persen dari area di dalam margin.
const framesOf = (layout) =>
  layout.slots.map(([c, r]) => {
    const [c0, c1] = c.split("/").map(Number);
    const [r0, r1] = r.split("/").map(Number);
    return { x: (c0 - 1) * 25 + 0.5, y: (r0 - 1) * 50 + 1, w: (c1 - c0) * 25 - 1, h: (r1 - r0) * 50 - 2 };
  });
const mkItem = (f, photoId = null) => ({ id: uid(), photoId, ...RESET, ...f });
const newSpread = (layoutId = "split", ids = []) => ({
  id: uid(), margin: 5, bg: "#FFFFFF", layout: layoutId,
  items: framesOf(layoutOf(layoutId)).map((f, i) => mkItem(f, ids[i] || null)),
});

// Susun daftar foto menjadi beberapa halaman tanpa menyisakan bingkai kosong.
const buildSpreads = (ids) => {
  const out = [];
  let i = 0, k = 0;
  while (i < ids.length) {
    const left = ids.length - i;
    let l = layoutOf(AUTO_ORDER[k++ % AUTO_ORDER.length]);
    if (l.slots.length > left) {
      l = LAYOUTS.filter((x) => x.slots.length <= left).sort((a, b) => b.slots.length - a.slots.length)[0];
    }
    out.push(newSpread(l.id, ids.slice(i, i + l.slots.length)));
    i += l.slots.length;
  }
  return out;
};

const fileIdFrom = (u) => (u.match(/\/d\/([\w-]+)/) || u.match(/[?&]id=([\w-]+)/) || [])[1];
const folderIdFrom = (u) => (u.match(/folders\/([\w-]+)/) || [])[1];
const driveThumb = (id) => `https://drive.google.com/thumbnail?id=${id}&sz=w1600`;

// Tempel ke garis bantu: tepi & tengah halaman, serta tepi & tengah bingkai lain.
const snap = (p, s, targets) => {
  let best = null;
  for (const t of targets) for (const k of [0, s / 2, s]) {
    const d = t - (p + k);
    if (Math.abs(d) < 1.2 && (!best || Math.abs(d) < Math.abs(best.d))) best = { d, t };
  }
  return best ? [p + best.d, best.t] : [p, null];
};

/* ---------- Geometri foto di dalam bingkai ---------- */
// Halaman berbentuk 2:1, jadi area dalam = (100 - 2m) x (50 - 2m) dalam satuan cqw.
const innerDims = (m) => ({ W: 100 - 2 * m, H: 50 - 2 * m });

// Ukuran foto (cqw) supaya selalu menutup penuh bingkai, berapapun sudut putarnya.
const photoGeom = (it, ar, margin) => {
  const { W, H } = innerDims(margin);
  const fw = (it.w / 100) * W, fh = (it.h / 100) * H;
  const th = (((it.rot || 0) + (it.tilt || 0)) * Math.PI) / 180;
  const c = Math.abs(Math.cos(th)), s = Math.abs(Math.sin(th));
  const hx = (fw / 2) * c + (fh / 2) * s;
  const hy = (fw / 2) * s + (fh / 2) * c;
  const ih = Math.max((2 * hx) / ar, 2 * hy) * it.z;
  return { fw, fh, th, hx, hy, iw: ih * ar, ih };
};

// Batasi geseran foto supaya tidak ada bagian kosong di dalam bingkai.
const clampOff = (it, g) => {
  const cos = Math.cos(g.th), sin = Math.sin(g.th);
  const ox = (it.ox / 100) * g.fw, oy = (it.oy / 100) * g.fh;
  const lx = ox * cos + oy * sin, ly = -ox * sin + oy * cos;
  const mx = Math.max(0, g.iw / 2 - g.hx), my = Math.max(0, g.ih / 2 - g.hy);
  const cx = clamp(lx, -mx, mx), cy = clamp(ly, -my, my);
  const nx = cx * cos - cy * sin, ny = cx * sin + cy * cos;
  return { ox: g.fw ? (nx / g.fw) * 100 : 0, oy: g.fh ? (ny / g.fh) * 100 : 0 };
};

// Ukuran bingkai awal untuk foto baru, mengikuti rasio fotonya.
const defaultFrame = (ar, margin) => {
  const { W, H } = innerDims(margin);
  let hc = H * 0.7, wc = hc * ar;
  if (wc > W * 0.6) { wc = W * 0.6; hc = wc / ar; }
  return { w: (wc / W) * 100, h: (hc / H) * 100 };
};

/* ---------- Satu spread (dua halaman buku) ---------- */
function Page({ spread, photoMap, edit, selId, crop, guides = {}, over, innerRef, onBg, onDrag, onCrop, onDropAt, onOver }) {
  return (
    <div
      style={{ containerType: "inline-size", background: spread.bg }}
      onPointerDown={edit ? (e) => e.target === e.currentTarget && onBg() : undefined}
      className="relative aspect-[2/1] w-full select-none overflow-hidden shadow-[0_1px_0_#E8E3D9,0_18px_40px_-24px_rgba(28,28,28,0.35)]"
    >
      <div
        ref={innerRef}
        style={{ position: "absolute", inset: `${spread.margin}cqw` }}
        onPointerDown={edit ? (e) => e.target === e.currentTarget && onBg() : undefined}
        onDragOver={edit ? (e) => { e.preventDefault(); onOver && onOver(null); } : undefined}
        onDrop={edit ? (e) => onDropAt(e, null) : undefined}
      >
        {spread.items.map((it, idx) => {
          const ph = photoMap[it.photoId];
          const sel = edit && selId === it.id;
          const cropping = sel && crop && !!ph;
          let imgStyle = null;
          if (ph) {
            const g = photoGeom(it, ph.ar || DEFAULT_AR, spread.margin);
            const o = clampOff(it, g);
            imgStyle = {
              position: "absolute", left: "50%", top: "50%", maxWidth: "none",
              width: `${g.iw}cqw`, height: `${g.ih}cqw`,
              transform: `translate(-50%, -50%) translate(${(o.ox / 100) * g.fw}cqw, ${(o.oy / 100) * g.fh}cqw) rotate(${(it.rot || 0) + (it.tilt || 0)}deg) scaleX(${it.flip ? -1 : 1})`,
            };
          }
          return (
            <div
              key={it.id}
              style={{ left: `${it.x}%`, top: `${it.y}%`, width: `${it.w}%`, height: `${it.h}%`, zIndex: cropping ? 45 : idx + 1, touchAction: "none" }}
              className={`absolute ${edit ? (cropping ? "cursor-move" : "cursor-grab active:cursor-grabbing") : ""} ${
                edit && over === it.id ? "outline outline-2 outline-[#B08D57]" : edit && !sel ? "hover:outline hover:outline-1 hover:outline-[#CFC8BA]" : ""}`}
              onPointerDown={edit ? (e) => onDrag(e, it, cropping ? "pan" : "move") : undefined}
              onDoubleClick={edit ? () => onCrop(it.id) : undefined}
              onDragOver={edit ? (e) => { e.preventDefault(); e.stopPropagation(); onOver && onOver(it.id); } : undefined}
              onDrop={edit ? (e) => { e.stopPropagation(); onDropAt(e, it.id); } : undefined}
            >
              {cropping && (
                <img src={ph.src} alt="" draggable={false} className="pointer-events-none object-cover opacity-40" style={imgStyle} />
              )}
              <div className={`relative h-full w-full overflow-hidden ${ph ? "bg-[#F1EEE8]" : "border border-dashed border-[#CFC8BA] bg-[#FBFAF8]"}`}>
                {ph ? (
                  <img src={ph.src} alt="" draggable={false} className="object-cover" style={imgStyle} />
                ) : (
                  edit && (
                    <div className="flex h-full flex-col items-center justify-center gap-1 text-center text-[#8A8A8A]">
                      <Plus className="h-5 w-5" />
                      <span className="px-2 font-sans text-xs">Seret foto ke sini</span>
                    </div>
                  )
                )}
              </div>
              {sel && (
                <>
                  <div className={`pointer-events-none absolute inset-0 outline outline-2 ${cropping ? "outline-dashed outline-[#B08D57]" : "outline-[#1C1C1C]"}`} />
                  {!cropping && HANDLES.map(([m, pos]) => (
                    <span key={m} onPointerDown={(e) => onDrag(e, it, m)} style={{ touchAction: "none" }}
                      className={`absolute h-3 w-3 border border-[#1C1C1C] bg-white before:absolute before:-inset-2 before:content-[''] ${pos}`} />
                  ))}
                </>
              )}
            </div>
          );
        })}
        {edit && guides.x != null && <div className="pointer-events-none absolute inset-y-0 z-50 w-px bg-[#B08D57]" style={{ left: `${guides.x}%` }} />}
        {edit && guides.y != null && <div className="pointer-events-none absolute inset-x-0 z-50 h-px bg-[#B08D57]" style={{ top: `${guides.y}%` }} />}
      </div>
      <div className="pointer-events-none absolute inset-y-0 left-1/2 z-40 w-px bg-gradient-to-b from-transparent via-black/10 to-transparent" />
    </div>
  );
}

function LayoutIcon({ layout, active }) {
  return (
    <span className={`grid aspect-[2/1] w-12 grid-cols-4 grid-rows-2 gap-[2px] border bg-white p-[3px] hover:border-[#1C1C1C] ${active ? "border-[#1C1C1C]" : "border-[#E8E3D9]"}`}>
      {layout.slots.map(([c, r], i) => <span key={i} style={{ gridColumn: c, gridRow: r }} className={active ? "bg-[#1C1C1C]" : "bg-[#CFC8BA]"} />)}
    </span>
  );
}

const Tool = ({ icon: Icon, label, onClick, disabled, danger, active }) => (
  <button type="button" onClick={onClick} disabled={disabled}
    className={`inline-flex items-center gap-1.5 border px-3 py-1.5 font-sans text-xs transition-colors disabled:opacity-40 disabled:hover:border-[#E8E3D9] ${
      active ? "border-[#1C1C1C] bg-[#1C1C1C] text-white" : "border-[#E8E3D9] bg-white hover:border-[#1C1C1C]"} ${danger ? "hover:text-red-700" : ""}`}>
    {Icon && <Icon className="h-3.5 w-3.5" />} {label}
  </button>
);

const Slider = ({ label, value, min, max, step, onChange, disabled, display }) => (
  <label className="flex items-center gap-3 font-sans text-sm">
    <span className="w-20 shrink-0 text-[#5C5C5C]">{label}</span>
    <input type="range" min={min} max={max} step={step} value={value} disabled={disabled}
      onChange={(e) => onChange(Number(e.target.value))} className="min-w-0 flex-1 accent-[#1C1C1C] disabled:opacity-40" />
    <span className="w-12 shrink-0 text-right text-[#8A8A8A]">{display}</span>
  </label>
);

/* ---------- Halaman utama ---------- */
export default function AlbumKostum() {
  const [title, setTitle] = useState("Album pernikahan kami");
  const [photos, setPhotos] = useState([]);
  // Riwayat: past (sebelum), now (sekarang), future (untuk ulangi).
  const [hist, setHist] = useState(() => ({ past: [], now: [newSpread("split")], future: [] }));
  const [cur, setCur] = useState(0);
  const [picked, setPicked] = useState([]);   // foto terpilih di daftar kiri (urut klik)
  const [selId, setSelId] = useState(null);   // bingkai terpilih di halaman
  const [crop, setCrop] = useState(false);
  const [guides, setGuides] = useState({ x: null, y: null });
  const [over, setOver] = useState(null);
  const [filter, setFilter] = useState("all");
  const [driveOpen, setDriveOpen] = useState(false);
  const [driveText, setDriveText] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(false);
  const fileRef = useRef(null);
  const innerRef = useRef(null);
  const coalesce = useRef({ key: null, t: 0 });

  const spreads = hist.now;
  const curSafe = Math.min(cur, spreads.length - 1);
  const spread = spreads[curSafe];
  const photoMap = useMemo(() => Object.fromEntries(photos.map((p) => [p.id, p])), [photos]);
  const used = useMemo(() => new Set(spreads.flatMap((s) => s.items.map((i) => i.photoId)).filter(Boolean)), [spreads]);
  const item = spread.items.find((i) => i.id === selId);
  const cropping = crop && !!item && !!item.photoId;
  const shown = photos.filter((p) => (filter === "fav" ? p.fav : filter === "unused" ? !used.has(p.id) : true));
  const arOf = (it) => photoMap[it.photoId]?.ar || DEFAULT_AR;

  /* ----- Riwayat (undo / redo) ----- */
  // Perubahan dengan key sama yang berdekatan (geser slider, seret bingkai) dihitung satu langkah.
  const commit = (fn, key = null, ttl = 800) => {
    const now = Date.now();
    const c = coalesce.current;
    const same = key !== null && c.key === key && now - c.t < ttl;
    coalesce.current = { key, t: now };
    setHist((h) => ({ past: same ? h.past : [...h.past.slice(-99), h.now], now: fn(h.now), future: [] }));
  };
  const undo = () => {
    coalesce.current = { key: null, t: 0 };
    setHist((h) => (h.past.length ? { past: h.past.slice(0, -1), now: h.past[h.past.length - 1], future: [h.now, ...h.future] } : h));
  };
  const redo = () => {
    coalesce.current = { key: null, t: 0 };
    setHist((h) => (h.future.length ? { past: [...h.past, h.now], now: h.future[0], future: h.future.slice(1) } : h));
  };

  const editSpread = (patch, key, ttl, at = curSafe) =>
    commit((all) => all.map((s, i) => (i === at ? { ...s, ...(typeof patch === "function" ? patch(s) : patch) } : s)), key, ttl);
  // patch: objek (digabung) atau fungsi (it, spread) => item baru.
  const editItem = (id, patch, key, ttl, at) =>
    editSpread((s) => {
      let geo = false;
      const items = s.items.map((it) => {
        if (it.id !== id) return it;
        const n = typeof patch === "function" ? patch(it, s) : { ...it, ...patch };
        if (n.x !== it.x || n.y !== it.y || n.w !== it.w || n.h !== it.h) geo = true;
        return n;
      });
      return geo ? { items, layout: null } : { items };
    }, key, ttl, at);
  // Gabungkan perubahan lalu rapikan geseran foto agar tetap menutup bingkai.
  const withClamp = (it, patch, s) => {
    const n = { ...it, ...patch };
    if (!n.photoId) return n;
    return { ...n, ...clampOff(n, photoGeom(n, arOf(n), s.margin)) };
  };

  useEffect(() => { if (!note) return; const t = setTimeout(() => setNote(""), 6000); return () => clearTimeout(t); }, [note]);
  useEffect(() => {
    if (!photos.length) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [photos.length]);
  useEffect(() => {
    const clear = () => setOver(null);
    window.addEventListener("dragend", clear); window.addEventListener("drop", clear);
    return () => { window.removeEventListener("dragend", clear); window.removeEventListener("drop", clear); };
  }, []);

  /* ----- Seret, ubah ukuran, atur posisi foto ----- */
  const startDrag = (e, it, mode) => {
    e.stopPropagation();
    if (e.button) return;
    if (selId !== it.id) setCrop(false);
    setSelId(it.id);
    if (!it.photoId && picked.length && mode === "move") { fillFrame(it.id, picked[0]); return; }
    if (mode === "pan" && !it.photoId) return;

    const at = curSafe;
    const s0 = spreads[at];
    const box = innerRef.current.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY, o = { ...it };
    const key = `g-${uid()}`;
    const others = s0.items.filter((i) => i.id !== it.id);
    const xs = [0, 50, 100, ...others.flatMap((i) => [i.x, i.x + i.w / 2, i.x + i.w])];
    const ys = [0, 50, 100, ...others.flatMap((i) => [i.y, i.y + i.h / 2, i.y + i.h])];
    const fwPx = (o.w * box.width) / 100, fhPx = (o.h * box.height) / 100;
    let geo = null, start = { ox: o.ox, oy: o.oy };
    if (mode === "pan") {
      geo = photoGeom(o, photoMap[o.photoId]?.ar || DEFAULT_AR, s0.margin);
      start = clampOff(o, geo);
    }
    let moved = false;

    const move = (ev) => {
      const px = ev.clientX - sx, py = ev.clientY - sy;
      if (!moved && Math.hypot(px, py) < 3) return;
      moved = true;
      const dx = (px / box.width) * 100, dy = (py / box.height) * 100;
      let n;
      if (mode === "pan") {
        n = clampOff({ ...o, ox: start.ox + (px / fwPx) * 100, oy: start.oy + (py / fhPx) * 100 }, geo);
      } else if (mode === "move") {
        const [x, gx] = ev.altKey ? [o.x + dx, null] : snap(o.x + dx, o.w, xs);
        const [y, gy] = ev.altKey ? [o.y + dy, null] : snap(o.y + dy, o.h, ys);
        n = { x: clamp(x, 0, 100 - o.w), y: clamp(y, 0, 100 - o.h) };
        setGuides({ x: gx, y: gy });
      } else {
        let l = o.x, r = o.x + o.w, t = o.y, b = o.y + o.h;
        if (mode.includes("w")) l = clamp(o.x + dx, 0, r - MIN);
        if (mode.includes("e")) r = clamp(o.x + o.w + dx, l + MIN, 100);
        if (mode.includes("n")) t = clamp(o.y + dy, 0, b - MIN);
        if (mode.includes("s")) b = clamp(o.y + o.h + dy, t + MIN, 100);
        n = { x: l, y: t, w: r - l, h: b - t };
      }
      editItem(o.id, n, key, Infinity, at);
    };
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", up);
      setGuides({ x: null, y: null });
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", up);
  };

  const toggleCrop = (id) => {
    const it = spread.items.find((i) => i.id === id);
    if (!it?.photoId) return;
    if (selId === id) setCrop((c) => !c);
    else { setSelId(id); setCrop(true); }
  };

  /* ----- Aksi pada foto & bingkai ----- */
  const fillFrame = (id, photoId) => {
    editItem(id, (it) => ({ ...it, photoId, ...RESET }));
    setPicked((a) => a.filter((x) => x !== photoId));
    setSelId(id); setCrop(false);
  };
  const addFrame = (photoId = null, center = null) => {
    const ar = photoId ? photoMap[photoId]?.ar || DEFAULT_AR : 1.2;
    const { w, h } = defaultFrame(ar, spread.margin);
    const n = spread.items.length;
    const it = mkItem({
      x: clamp(center ? center.x - w / 2 : 50 - w / 2 + n * 3, 0, 100 - w),
      y: clamp(center ? center.y - h / 2 : 50 - h / 2 + n * 3, 0, 100 - h), w, h,
    }, photoId);
    editSpread((s) => ({ items: [...s.items, it], layout: null }));
    setSelId(it.id); setCrop(false);
  };
  // Isi bingkai kosong lebih dulu; kalau foto lebih banyak, sisanya jadi halaman baru.
  const placePhotos = (ids) => {
    if (!ids.length) return;
    const at = curSafe;
    const rest = [...ids];
    const items = spread.items.map((it) =>
      !it.photoId && rest.length ? { ...it, photoId: rest.shift(), ...RESET } : it);
    const filled = ids.length - rest.length;
    if (!filled && ids.length === 1) { addFrame(ids[0]); setPicked([]); return; }
    const extra = buildSpreads(rest);
    commit((all) => {
      const next = all.map((s, i) => (i === at ? { ...s, items } : s));
      next.splice(at + 1, 0, ...extra);
      return next;
    });
    setPicked([]);
    if (extra.length) { setCur(at + 1); setNote(`${ids.length} foto ditambahkan. ${extra.length} halaman baru dibuat untuk sisanya.`); }
  };
  const dropAt = (e, itemId) => {
    e.preventDefault();
    setOver(null);
    const id = e.dataTransfer.getData("text/plain");
    if (!photoMap[id]) return;
    if (itemId) { fillFrame(itemId, id); return; }
    const box = innerRef.current.getBoundingClientRect();
    addFrame(id, { x: ((e.clientX - box.left) / box.width) * 100, y: ((e.clientY - box.top) / box.height) * 100 });
  };
  const setZoom = (z) => editItem(item.id, (it, s) => withClamp(it, { z }, s), `zoom-${item.id}`, 800);
  const setTilt = (tilt) => editItem(item.id, (it, s) => withClamp(it, { tilt }, s), `tilt-${item.id}`, 800);
  const rotate = (deg) => editItem(item.id, (it, s) => withClamp(it, { rot: (((it.rot || 0) + deg) % 360 + 360) % 360 }, s));
  const flip = () => editItem(item.id, (it) => ({ ...it, flip: !it.flip }));
  const resetPhoto = () => editItem(item.id, RESET);
  // Ubah bentuk bingkai mengikuti rasio asli foto (tanpa terpotong).
  const fitFrame = () => editItem(item.id, (it, s) => {
    const { W, H } = innerDims(s.margin);
    const ar = arOf(it);
    const eff = (it.rot || 0) % 180 === 90 ? 1 / ar : ar;
    let wc = (it.w / 100) * W, hc = wc / eff;
    if (hc > H) { hc = H; wc = hc * eff; }
    if (wc > W) { wc = W; hc = wc / eff; }
    const w = (wc / W) * 100, h = (hc / H) * 100;
    return { ...it, x: clamp(it.x + it.w / 2 - w / 2, 0, 100 - w), y: clamp(it.y + it.h / 2 - h / 2, 0, 100 - h), w, h, z: 1, ox: 0, oy: 0, tilt: 0 };
  });
  const delItem = () => { editSpread((s) => ({ items: s.items.filter((i) => i.id !== selId), layout: null })); setSelId(null); setCrop(false); };
  const dupItem = () => {
    const c = { ...item, id: uid(), x: clamp(item.x + 3, 0, 100 - item.w), y: clamp(item.y + 4, 0, 100 - item.h) };
    editSpread((s) => ({ items: [...s.items, c], layout: null })); setSelId(c.id);
  };
  const reorder = (toFront) =>
    editSpread((s) => {
      const rest = s.items.filter((i) => i.id !== selId);
      return { items: toFront ? [...rest, item] : [item, ...rest] };
    });
  const applyLayout = (id) => {
    const ids = spread.items.map((i) => i.photoId).filter(Boolean);
    const next = newSpread(id, ids);
    editSpread({ items: next.items, layout: id });
    setSelId(null); setCrop(false);
    if (ids.length > next.items.length) setNote(`${ids.length - next.items.length} foto tidak muat di template ini dan kembali ke daftar foto.`);
  };

  /* ----- Keyboard ----- */
  useEffect(() => {
    const onKey = (e) => {
      const tag = e.target.tagName;
      const isRange = tag === "INPUT" && e.target.type === "range";
      const typing = tag === "TEXTAREA" || (tag === "INPUT" && !isRange && e.target.type !== "checkbox");
      if (typing) return;
      if (preview) {
        if (e.key === "Escape") setPreview(false);
        if (e.key === "ArrowLeft") setCur(Math.max(0, curSafe - 1));
        if (e.key === "ArrowRight") setCur(Math.min(spreads.length - 1, curSafe + 1));
        return;
      }
      if (e.ctrlKey || e.metaKey) {
        const k = e.key.toLowerCase();
        if (k === "z") { e.preventDefault(); e.shiftKey ? redo() : undo(); }
        else if (k === "y") { e.preventDefault(); redo(); }
        else if (k === "d" && item) { e.preventDefault(); dupItem(); }
        return;
      }
      if (!item) return;
      const step = e.shiftKey ? 2 : 0.5;
      const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
      if (e.key === "Delete" || e.key === "Backspace") { e.preventDefault(); delItem(); }
      else if (e.key === "Escape") { setSelId(null); setCrop(false); }
      else if (e.key === "Enter" && tag !== "BUTTON") { e.preventDefault(); toggleCrop(item.id); }
      else if (d && !isRange) {
        e.preventDefault();
        editItem(item.id, { x: clamp(item.x + d[0], 0, 100 - item.w), y: clamp(item.y + d[1], 0, 100 - item.h) }, `key-${item.id}`, 800);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  /* ----- Halaman album ----- */
  const addSpread = () => { commit((a) => [...a, newSpread()]); setCur(spreads.length); };
  const dupSpread = () => {
    const copy = { ...spread, id: uid(), items: spread.items.map((i) => ({ ...i, id: uid() })) };
    commit((a) => { const b = [...a]; b.splice(curSafe + 1, 0, copy); return b; });
    setCur(curSafe + 1);
  };
  const removeSpread = () => {
    if (spreads.length === 1) return;
    commit((a) => a.filter((_, i) => i !== curSafe));
    setCur(Math.max(0, curSafe - 1));
  };
  const moveSpread = (dir) => {
    const to = curSafe + dir;
    if (to < 0 || to >= spreads.length) return;
    commit((a) => { const b = [...a]; [b[curSafe], b[to]] = [b[to], b[curSafe]]; return b; });
    setCur(to);
  };

  /* ----- Foto: unggah, Drive, favorit ----- */
  // Baca ukuran asli foto supaya bingkai & putaran akurat.
  const measure = (list) => list.forEach((p) => {
    const im = new Image();
    im.onload = () => setPhotos((a) => a.map((x) => (x.id === p.id ? { ...x, ar: im.naturalWidth / im.naturalHeight } : x)));
    im.src = p.src;
  });
  const addPhotos = (list) => {
    const have = new Set(photos.map((p) => p.src));
    const fresh = list.filter((p) => !have.has(p.src));
    if (!fresh.length) return;
    setPhotos((prev) => [...prev, ...fresh]);
    measure(fresh);
  };
  const onFiles = (e) => {
    addPhotos(Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"))
      .map((f) => ({ id: uid(), src: URL.createObjectURL(f), name: f.name, fav: false })));
    e.target.value = "";
  };
  const importDrive = async () => {
    const lines = driveText.split(/\s+/).filter(Boolean);
    if (!lines.length) return;
    setBusy(true); setNote("");
    const found = [];
    let skipped = false;
    for (const link of lines) {
      const folder = folderIdFrom(link);
      if (folder) {
        if (!GOOGLE_KEY) { skipped = true; continue; }
        try {
          const q = encodeURIComponent(`'${folder}' in parents and mimeType contains 'image/'`);
          const res = await fetch(`https://www.googleapis.com/drive/v3/files?q=${q}&fields=files(id,name)&pageSize=200&key=${GOOGLE_KEY}`);
          const data = await res.json();
          (data.files || []).forEach((f) => found.push({ id: uid(), src: driveThumb(f.id), name: f.name, fav: false }));
        } catch { setNote("Folder tidak bisa dibaca. Pastikan aksesnya \"Siapa saja yang memiliki link\"."); }
      } else if (fileIdFrom(link)) {
        found.push({ id: uid(), src: driveThumb(fileIdFrom(link)), name: "Foto Drive", fav: false });
      }
    }
    addPhotos(found);
    setBusy(false);
    if (skipped) setNote("Impor folder butuh REACT_APP_GOOGLE_API_KEY di file .env. Untuk sementara, tempel link foto satu per satu.");
    else if (!found.length) setNote("Tidak ada foto yang ditemukan dari link itu.");
    else { setDriveText(""); setDriveOpen(false); }
  };
  const autoBuild = () => {
    const pool = photos.filter((p) => !used.has(p.id)).sort((a, b) => Number(b.fav) - Number(a.fav));
    if (!pool.length) { setNote("Semua foto sudah dipakai. Tambah foto baru untuk disusun otomatis."); return; }
    const base = spreads.filter((s) => s.items.some((i) => i.photoId));
    const made = buildSpreads(pool.map((p) => p.id));
    commit(() => [...base, ...made]);
    setCur(base.length); setSelId(null); setCrop(false);
    setNote(`${made.length} halaman disusun otomatis. Foto favorit dipakai lebih dulu.`);
  };
  const togglePick = (id) => setPicked((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));
  const toggleFav = (id) => setPhotos((a) => a.map((p) => (p.id === id ? { ...p, fav: !p.fav } : p)));
  const removePhoto = (id) => {
    setPhotos((a) => a.filter((p) => p.id !== id));
    commit((all) => all.map((s) => ({ ...s, items: s.items.map((i) => (i.photoId === id ? { ...i, photoId: null } : i)) })));
    setPicked((a) => a.filter((x) => x !== id));
  };

  const pageProps = { photoMap, guides };
  const hint = cropping ? "Geser foto di dalam bingkai untuk mengatur posisinya. Pakai slider Zoom dan Miringkan, lalu klik Selesai."
    : item ? "Seret untuk memindahkan, tarik sudut atau sisi untuk mengubah ukuran. Klik dua kali untuk mengatur posisi foto. Ctrl+Z untuk membatalkan."
    : picked.length ? "Klik bingkai kosong untuk menaruh foto terpilih, atau tekan \"Tambah ke halaman\"."
    : "Seret foto dari kiri ke halaman, atau klik dua kali foto untuk menambahkannya.";

  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#1C1C1C]">
      <header className="border-b border-[#E8E3D9] px-6 py-5 md:px-12">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-4">
          <Link to="/" className="flex items-center gap-2 font-sans text-sm text-[#5C5C5C] hover:text-[#1C1C1C]">
            <ArrowLeft className="h-4 w-4" /> Beranda
          </Link>
          <input value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Judul album"
            className="min-w-0 flex-1 border-b border-transparent bg-transparent font-serif text-3xl font-light tracking-tight outline-none focus:border-[#1C1C1C] md:text-4xl" />
          <span className="font-sans text-sm text-[#8A8A8A]">{spreads.length} halaman · {photos.length} foto</span>
          <div className="flex items-center">
            <button type="button" onClick={undo} disabled={!hist.past.length} title="Batalkan (Ctrl+Z)" aria-label="Batalkan"
              className="p-2 hover:bg-[#EFEBE3] disabled:opacity-30"><Undo2 className="h-5 w-5" /></button>
            <button type="button" onClick={redo} disabled={!hist.future.length} title="Ulangi (Ctrl+Shift+Z)" aria-label="Ulangi"
              className="p-2 hover:bg-[#EFEBE3] disabled:opacity-30"><Redo2 className="h-5 w-5" /></button>
          </div>
          <button type="button" onClick={() => setPreview(true)}
            className="inline-flex items-center gap-2 border border-[#E8E3D9] px-4 py-2 font-sans text-sm transition-colors hover:border-[#1C1C1C]">
            <Eye className="h-4 w-4" /> Pratinjau
          </button>
          <button type="button" onClick={autoBuild} disabled={!photos.length}
            className="inline-flex items-center gap-2 border border-[#1C1C1C] px-4 py-2 font-sans text-sm transition-colors hover:bg-[#1C1C1C] hover:text-white disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-inherit">
            <Wand2 className="h-4 w-4" /> Susun otomatis
          </button>
        </div>
      </header>

      <div className="mx-auto grid max-w-[1400px] gap-8 px-6 py-8 md:px-12 lg:grid-cols-[320px_1fr]">
        {/* Kolom foto */}
        <aside className="flex flex-col border border-[#E8E3D9] bg-white lg:max-h-[calc(100vh-170px)]">
          <div className="space-y-3 border-b border-[#E8E3D9] p-4">
            <div className="flex gap-2">
              <button type="button" onClick={() => fileRef.current?.click()}
                className="flex flex-1 items-center justify-center gap-2 bg-[#1C1C1C] px-3 py-2.5 font-sans text-sm text-white hover:bg-black">
                <ImagePlus className="h-4 w-4" /> Dari perangkat
              </button>
              <button type="button" onClick={() => setDriveOpen((v) => !v)}
                className={`flex flex-1 items-center justify-center gap-2 border px-3 py-2.5 font-sans text-sm ${driveOpen ? "border-[#1C1C1C]" : "border-[#E8E3D9] hover:border-[#1C1C1C]"}`}>
                <Link2 className="h-4 w-4" /> Dari Drive
              </button>
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onFiles} />
            </div>
            {driveOpen && (
              <div className="space-y-2">
                <textarea value={driveText} onChange={(e) => setDriveText(e.target.value)} rows={3}
                  placeholder="Tempel link folder atau foto Google Drive, satu per baris"
                  className="w-full resize-none border border-[#E8E3D9] p-3 font-sans text-sm outline-none focus:border-[#1C1C1C]" />
                <p className="font-sans text-xs leading-relaxed text-[#8A8A8A]">Atur akses link menjadi &quot;Siapa saja yang memiliki link&quot; supaya foto bisa tampil.</p>
                <button type="button" onClick={importDrive} disabled={busy}
                  className="w-full border border-[#1C1C1C] py-2 font-sans text-sm hover:bg-[#1C1C1C] hover:text-white disabled:opacity-50">
                  {busy ? "Mengambil foto…" : "Ambil foto"}
                </button>
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              {[["all", "Semua"], ["fav", "Favorit"], ["unused", "Belum dipakai"]].map(([id, label]) => (
                <button key={id} type="button" onClick={() => setFilter(id)}
                  className={`px-3 py-1 font-sans text-xs ${filter === id ? "bg-[#1C1C1C] text-white" : "border border-[#E8E3D9] text-[#5C5C5C] hover:border-[#1C1C1C]"}`}>
                  {label}
                </button>
              ))}
            </div>
            {shown.length > 0 && (
              <div className="flex items-center justify-between gap-2 font-sans text-xs">
                <button type="button" onClick={() => setPicked(picked.length ? [] : shown.map((p) => p.id))}
                  className="text-[#5C5C5C] underline-offset-2 hover:text-[#1C1C1C] hover:underline">
                  {picked.length ? `Batal pilih (${picked.length})` : "Pilih semua"}
                </button>
                {picked.length > 0 && (
                  <button type="button" onClick={() => placePhotos(picked)}
                    className="border border-[#1C1C1C] px-3 py-1.5 hover:bg-[#1C1C1C] hover:text-white">
                    Tambah ke halaman
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="min-h-[220px] flex-1 overflow-y-auto p-3">
            {shown.length === 0 ? (
              <div className="flex h-full min-h-[200px] flex-col items-center justify-center px-6 text-center">
                <p className="font-serif text-2xl font-light">{photos.length ? "Tidak ada foto di filter ini" : "Mulai dari fotomu"}</p>
                <p className="mt-2 font-sans text-sm leading-relaxed text-[#8A8A8A]">
                  {photos.length ? "Ganti filter untuk melihat foto lain." : "Unggah dari perangkat atau tempel link Google Drive di atas."}
                </p>
                {!photos.length && (
                  <button type="button" onClick={() => fileRef.current?.click()}
                    className="mt-4 border border-[#1C1C1C] px-4 py-2 font-sans text-sm hover:bg-[#1C1C1C] hover:text-white">
                    Pilih foto
                  </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {shown.map((p) => {
                  const pos = picked.indexOf(p.id);
                  return (
                    <div key={p.id} draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", p.id)}
                      className={`group relative aspect-square cursor-grab overflow-hidden bg-[#F1EEE8] ${pos > -1 ? "ring-2 ring-[#1C1C1C] ring-offset-2" : ""}`}>
                      <button type="button" onClick={() => togglePick(p.id)} onDoubleClick={() => { placePhotos([p.id]); setPicked((a) => a.filter((x) => x !== p.id)); }}
                        aria-label={`Pilih foto ${p.name}`} className="absolute inset-0 h-full w-full">
                        <img src={p.src} alt={p.name} loading="lazy" draggable={false}
                          className={`h-full w-full object-cover ${used.has(p.id) ? "opacity-50" : ""}`} />
                      </button>
                      {used.has(p.id) && (
                        <span className="pointer-events-none absolute bottom-1 left-1 flex h-5 w-5 items-center justify-center bg-[#1C1C1C] text-white"><Check className="h-3 w-3" /></span>
                      )}
                      {pos > -1 && picked.length > 1 && (
                        <span className="pointer-events-none absolute bottom-1 right-1 flex h-5 min-w-[20px] items-center justify-center bg-[#B08D57] px-1 font-sans text-[10px] text-white">{pos + 1}</span>
                      )}
                      <button type="button" onClick={() => toggleFav(p.id)} aria-label="Tandai favorit"
                        className={`absolute right-1 top-1 flex h-6 w-6 items-center justify-center bg-white/90 ${p.fav ? "" : "opacity-0 focus:opacity-100 group-hover:opacity-100"}`}>
                        <Heart className={`h-3.5 w-3.5 ${p.fav ? "fill-[#1C1C1C]" : ""}`} />
                      </button>
                      <button type="button" onClick={() => removePhoto(p.id)} aria-label="Hapus dari daftar"
                        className="absolute left-1 top-1 flex h-6 w-6 items-center justify-center bg-white/90 opacity-0 focus:opacity-100 group-hover:opacity-100">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </aside>

        {/* Kolom editor */}
        <main className="min-w-0">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setCur(Math.max(0, curSafe - 1))} disabled={curSafe === 0}
                aria-label="Halaman sebelumnya" className="p-2 hover:bg-[#EFEBE3] disabled:opacity-30"><ChevronLeft className="h-5 w-5" /></button>
              <span className="min-w-[130px] text-center font-serif text-xl">Halaman {curSafe + 1} dari {spreads.length}</span>
              <button type="button" onClick={() => setCur(Math.min(spreads.length - 1, curSafe + 1))} disabled={curSafe === spreads.length - 1}
                aria-label="Halaman berikutnya" className="p-2 hover:bg-[#EFEBE3] disabled:opacity-30"><ChevronRight className="h-5 w-5" /></button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-sans text-xs text-[#8A8A8A]">Template</span>
              {LAYOUTS.map((l) => (
                <button key={l.id} type="button" title={l.name} aria-label={`Template ${l.name}`} aria-pressed={spread.layout === l.id} onClick={() => applyLayout(l.id)}>
                  <LayoutIcon layout={l} active={spread.layout === l.id} />
                </button>
              ))}
            </div>
          </div>

          {/* Toolbar bingkai terpilih */}
          <div className="mb-3 min-h-[112px] border border-[#E8E3D9] bg-white p-3">
            {item ? (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Tool icon={Crop} label={cropping ? "Selesai" : "Atur posisi"} active={cropping} disabled={!item.photoId} onClick={() => setCrop((c) => !c)} />
                  <Tool icon={RotateCcw} label="Putar kiri" disabled={!item.photoId} onClick={() => rotate(-90)} />
                  <Tool icon={RotateCw} label="Putar kanan" disabled={!item.photoId} onClick={() => rotate(90)} />
                  <Tool icon={FlipHorizontal} label="Balik" disabled={!item.photoId} onClick={flip} />
                  <Tool icon={Frame} label="Sesuaikan bingkai" disabled={!item.photoId} onClick={fitFrame} />
                  <Tool icon={Maximize2} label="Penuhi halaman" onClick={() => editItem(item.id, { x: 0, y: 0, w: 100, h: 100 })} />
                  <Tool icon={ImagePlus} label="Ganti foto" disabled={!picked.length} onClick={() => fillFrame(item.id, picked[0])} />
                  <span className="mx-1 hidden h-5 w-px bg-[#E8E3D9] sm:block" />
                  <Tool icon={Copy} label="Duplikat" onClick={dupItem} />
                  <Tool icon={ChevronsUp} label="Ke depan" onClick={() => reorder(true)} />
                  <Tool icon={ChevronsDown} label="Ke belakang" onClick={() => reorder(false)} />
                  <Tool icon={Trash2} label="Hapus" danger onClick={delItem} />
                </div>
                <div className="grid items-center gap-x-6 gap-y-2 sm:grid-cols-[1fr_1fr_auto]">
                  <Slider label="Zoom" min={1} max={4} step={0.01} value={item.z} disabled={!item.photoId} onChange={setZoom} display={`${item.z.toFixed(1)}x`} />
                  <Slider label="Miringkan" min={-45} max={45} step={0.5} value={item.tilt || 0} disabled={!item.photoId} onChange={setTilt} display={`${item.tilt || 0}°`} />
                  <Tool label="Reset foto" disabled={!item.photoId} onClick={resetPhoto} />
                </div>
              </div>
            ) : (
              <p className="flex h-full min-h-[84px] items-center justify-center text-center font-sans text-sm text-[#8A8A8A]">
                Klik sebuah bingkai di halaman untuk mengedit: pindah, ubah ukuran, atur posisi, putar, atau ganti foto.
              </p>
            )}
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={spread.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}
              className="bg-[#EFEBE3] p-4 md:p-8">
              <Page edit spread={spread} {...pageProps} selId={selId} crop={crop} over={over} innerRef={innerRef}
                onBg={() => { setSelId(null); setCrop(false); }} onDrag={startDrag}
                onCrop={toggleCrop} onDropAt={dropAt} onOver={setOver} />
            </motion.div>
          </AnimatePresence>

          <p className="mt-3 min-h-[20px] font-sans text-sm text-[#8A8A8A]" aria-live="polite">{note || hint}</p>

          {/* Pengaturan halaman */}
          <div className="mt-4 space-y-3 border border-[#E8E3D9] bg-white p-4">
            <h3 className="font-serif text-xl font-light">Halaman</h3>
            <div className="grid gap-4 md:grid-cols-2">
              <Slider label="Margin" min={0} max={12} step={0.5} value={spread.margin} display={String(spread.margin)}
                onChange={(margin) => editSpread({ margin }, "margin", 800)} />
              <div className="flex items-center gap-3 font-sans text-sm">
                <span className="w-20 shrink-0 text-[#5C5C5C]">Warna latar</span>
                <div className="flex gap-2">
                  {BGS.map((c) => (
                    <button key={c} type="button" aria-label={`Latar ${c}`} onClick={() => editSpread({ bg: c })} style={{ background: c }}
                      className={`h-7 w-7 border ${spread.bg === c ? "ring-2 ring-[#1C1C1C] ring-offset-2" : "border-[#CFC8BA]"}`} />
                  ))}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Tool icon={Plus} label="Tambah bingkai kosong" onClick={() => addFrame()} />
            </div>
          </div>

          {/* Daftar halaman */}
          <div className="mt-6 border-t border-[#E8E3D9] pt-5">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="font-serif text-2xl font-light">Semua halaman</h2>
              <div className="flex gap-1">
                <button type="button" onClick={() => moveSpread(-1)} disabled={curSafe === 0} aria-label="Geser halaman ke kiri" className="p-2 hover:bg-[#EFEBE3] disabled:opacity-30"><ChevronLeft className="h-4 w-4" /></button>
                <button type="button" onClick={() => moveSpread(1)} disabled={curSafe === spreads.length - 1} aria-label="Geser halaman ke kanan" className="p-2 hover:bg-[#EFEBE3] disabled:opacity-30"><ChevronRight className="h-4 w-4" /></button>
                <button type="button" onClick={dupSpread} aria-label="Duplikat halaman ini" title="Duplikat halaman" className="p-2 hover:bg-[#EFEBE3]"><Copy className="h-4 w-4" /></button>
                <button type="button" onClick={removeSpread} disabled={spreads.length === 1} aria-label="Hapus halaman ini" title="Hapus halaman" className="p-2 hover:bg-[#EFEBE3] disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-3">
              {spreads.map((s, i) => (
                <button key={s.id} type="button" onClick={() => setCur(i)} aria-label={`Buka halaman ${i + 1}`}
                  className={`w-40 shrink-0 border-2 p-1 text-left transition-colors ${i === curSafe ? "border-[#1C1C1C]" : "border-transparent hover:border-[#CFC8BA]"}`}>
                  <Page spread={s} {...pageProps} />
                  <span className="mt-1 block font-sans text-xs text-[#8A8A8A]">{i + 1}</span>
                </button>
              ))}
              <button type="button" onClick={addSpread} aria-label="Tambah halaman"
                className="flex aspect-[2/1] w-40 shrink-0 flex-col items-center justify-center gap-1 self-start border border-dashed border-[#CFC8BA] text-[#8A8A8A] hover:border-[#1C1C1C] hover:text-[#1C1C1C]">
                <Plus className="h-5 w-5" />
                <span className="font-sans text-xs">Tambah halaman</span>
              </button>
            </div>
          </div>
        </main>
      </div>

      {/* Pratinjau */}
      <AnimatePresence>
        {preview && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[100] flex flex-col bg-[#F9F8F6]">
            <div className="flex items-center justify-between px-6 py-4 md:px-12">
              <span className="font-serif text-2xl font-light">{title}</span>
              <button type="button" onClick={() => setPreview(false)} aria-label="Tutup pratinjau" className="p-2 hover:bg-[#EFEBE3]"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex flex-1 items-center justify-center gap-2 px-4 md:px-8">
              <button type="button" onClick={() => setCur(Math.max(0, curSafe - 1))} disabled={curSafe === 0}
                aria-label="Halaman sebelumnya" className="p-2 hover:bg-[#EFEBE3] disabled:opacity-30"><ChevronLeft className="h-6 w-6" /></button>
              <div className="w-full max-w-[1100px]"><Page spread={spread} {...pageProps} /></div>
              <button type="button" onClick={() => setCur(Math.min(spreads.length - 1, curSafe + 1))} disabled={curSafe === spreads.length - 1}
                aria-label="Halaman berikutnya" className="p-2 hover:bg-[#EFEBE3] disabled:opacity-30"><ChevronRight className="h-6 w-6" /></button>
            </div>
            <p className="py-6 text-center font-sans text-sm text-[#8A8A8A]">Halaman {curSafe + 1} dari {spreads.length}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}